import en from './en.json' with { type: 'json' };
import ms from './ms.json' with { type: 'json' };
import zh from './zh.json' with { type: 'json' };
import ta from './ta.json' with { type: 'json' };

import type { Locale } from '@kilat/shared/schemas/primitives';

export const defaultI18n: Readonly<Record<Locale, Record<string, unknown>>> = {
  en,
  ms,
  zh,
  ta,
};

/**
 * Load ONE locale's catalogue on demand.
 *
 * `defaultI18n` above statically imports all four, which is right for the seed
 * script and the Cloud Functions (they need every language in one process) and
 * badly wrong for a browser: the four JSON files total 1.25 MB, so every app
 * importing that barrel shipped all of them — every language, and every
 * namespace, including the 1,788-key `dashboard.*` tree on the marketing site
 * that never renders one of them.
 *
 * Measured 2026-08-22: marketing's entry chunk was 1.5 MB, of which roughly
 * 1.2 MB was this. On simulated mobile the JS saturated the pipe before the
 * render-blocking stylesheet could win it, holding first contentful paint at
 * 8-12s against a 1.5s budget.
 *
 * Each `import()` is a separate chunk, so a visitor downloads the language they
 * are actually reading.
 */
export async function loadLocaleCatalog(locale: Locale): Promise<Record<string, unknown>> {
  // No `with { type: 'json' }` on these dynamic imports, unlike the static ones
  // above. Vite's dev server serves .json as text/javascript, and strict MIME
  // checking then rejects it as a JSON module — which broke all 42 marketing
  // e2e tests with a blank page. Vite parses a plain `import('./x.json')`
  // correctly in both dev and build.
  switch (locale) {
    case 'ms':
      return (await import('./ms.json')).default;
    case 'zh':
      return (await import('./zh.json')).default;
    case 'ta':
      return (await import('./ta.json')).default;
    default:
      return (await import('./en.json')).default;
  }
}
